/* eslint-disable max-lines */
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router, Event as NavigationEvent, NavigationEnd } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, from, Subject, combineLatest } from 'rxjs';
import { filter, finalize, switchMap, takeUntil } from 'rxjs/operators';

import {
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  ISitemateAssignedUserWithTaskUrls,
} from '@site-mate/dashpivot-shared-library';
import { CompanyPlanTypes } from '@site-mate/sitemate-global-shared';

import { TemplateLinksTeamService } from 'app/apps/templates-link/template-links-team.service';
import { TemplatesService } from 'app/apps/templates.service';
import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { SegmentService } from 'app/segment/segment.service';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { TeamWeb } from 'app/shared/model/team.model';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { TaskService } from 'app/shared/service/task.service';
import { TeamService } from 'app/shared/service/team.service';
import { StateToggleTheme } from 'app/ui-components/state-toggle/state-toggle-theme.enum';
import { AccountUpgradeLimitModalContext } from 'app/user/account-upgrade/account-upgrade-limit/account-upgrade-limit-context.enum';
import { AccountUpgradeLimitService } from 'app/user/account-upgrade/account-upgrade-limit/account-upgrade-limit.service';
import { UserService } from 'app/user/user.service';
import { SortOrder } from 'app/users/user-list/sort-order.enum';
import { UserListService } from 'app/users/user-list/user-list.service';
import { IUser } from 'app/users/user-list.model';

@Component({
  selector: 'cc-user-forms',
  templateUrl: './form-users.component.html',
  styleUrls: ['./form-users.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class FormUsersComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  @ViewChild('templateLinksModal') private readonly templateLinksModal: TemplateRef<any>;
  readonly appHierarchy = { ...AppHierarchy };
  readonly companyPlanTypes = { ...CompanyPlanTypes };

  assignedUsers: ISitemateAssignedUserWithTaskUrls[] = [];
  assignedUser: ISitemateAssignedUserWithTaskUrls;
  isLoading = true;
  hierarchy: AppHierarchy;
  hierarchyId: string;
  path: string;
  team: TeamWeb;
  isTeamControllerOrAbove: boolean;
  companyPlanType: CompanyPlanTypes;

  readonly defaultSortHeader = 'firstName';
  readonly defaultSortOrder = SortOrder.ASC;
  readonly tableHeaders = [
    { name: 'firstName', label: 'First Name' },
    { name: 'lastName', label: 'Last Name' },
    { name: 'companyName', label: 'Company' },
    { name: 'taskLinks', label: 'Sitemate Links Accessed' },
  ];

  checkedUsers = new Set<string>();

  sortingColumn = this.defaultSortHeader;
  currentSortOrder: SortOrder = this.defaultSortOrder;

  sortedList: IUser[] = [];
  filteredList: IUser[] = [];
  searchTerm = '';
  loadingTemplates: boolean;
  url: string;
  stateToggleTheme = StateToggleTheme.White;
  upgradePlanMessage: string;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly errorHandler: ErrorHandler,
    private readonly ngbModal: NgbModal,
    private readonly teamService: TeamService,
    private readonly taskService: TaskService,
    private readonly segmentService: SegmentService,
    private readonly confirmService: ConfirmService,
    private readonly userService: UserService,
    private readonly userListService: UserListService,
    private readonly linksService: TemplateLinksTeamService,
    private readonly router: Router,
    private readonly routeParamsService: RouteParamsService,
    private readonly templatesService: TemplatesService,
    private readonly accountUpgradeLimitService: AccountUpgradeLimitService,
    private readonly i18nService: TmpI18NService,
  ) {}

  ngOnInit() {
    this.upgradePlanMessage = this.i18nService.getMessage('upgradePlanToUnlockFeature');

    this.checkCredentials();
    this.redirectToLinks();
    this.loadUsers();
  }

  checkCredentials() {
    const { hierarchy } = this.activatedRoute.snapshot.data;
    this.userService
      .isTeamControllerOrAbove$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((canEdit) => {
        this.isTeamControllerOrAbove = canEdit;
      });

    this.hierarchy = hierarchy;
  }

  private redirectToLinks() {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((e: NavigationEvent) => e instanceof NavigationEnd),
      )
      .subscribe((event: NavigationEnd) => {
        this.url = event.url.split('/').pop();
      });

    if (this.appHierarchy.Team === this.hierarchy) {
      void this.router.navigate(['./links'], { relativeTo: this.activatedRoute });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.routeParamsService.set('companyFromTeam', null);
    this.routeParamsService.set('templates', null);
  }

  onTemplateLinkAccessed({ id }) {
    this.assignedUser = this.assignedUsers.find(({ userId }) => userId === id);
    this.ngbModal.open(this.templateLinksModal, {
      windowClass: 'sitemate-template-lists-link-modal',
    });
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.SitemateLinksAccessedViewed, {
        Context: 'Form User',
        Hierarchy: this.hierarchy,
      }),
      this.segmentService,
    );
  }

  private loadUsers() {
    this.teamService
      .getCurrentTeam$()
      .pipe(
        filter((team) => team && team.id),
        switchMap(this.processTeam.bind(this)),
        takeUntil(this.destroy$),
      )
      .subscribe((users) => {
        this.assignedUsers = this.updateUsersHierarchy(users);
        this.updateUsersLists(this.assignedUsers);
        this.loadTemplatesAndLinks();
      }, this.errorHandler.handle.bind(this.errorHandler));
  }

  private updateUsersHierarchy(
    users: ISitemateAssignedUserWithTaskUrls[],
  ): ISitemateAssignedUserWithTaskUrls[] {
    return users.map((user) => {
      user.taskUrls = user.taskUrls.map(this.removeCompanyFromTaskHierarchy);
      return user;
    });
  }

  private removeCompanyFromTaskHierarchy({ url, hierarchy, taskName }) {
    return {
      url,
      hierarchy: hierarchy.split('/').slice(1).join('/'),
      taskName,
    };
  }

  private processTeam(team: TeamWeb) {
    this.path = this.teamService.getPath(team);
    this.team = team;
    this.hierarchyId = team.id;
    this.companyPlanType = team.companyMetadata.billing.planType;
    return this.getAssignedUsers();
  }

  private getAssignedUsers() {
    this.isLoading = true;
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.UserTypeViewed, {
        Context: 'Form User',
        Hierarchy: this.hierarchy,
      }),
      this.segmentService,
    );
    return this.taskService.getAssignedUsers(this.hierarchy, this.team.id).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isLoading = false;
      }),
    );
  }

  bulkRemoveUsers() {
    const usersToRemove = Array.from(this.checkedUsers);

    return forkJoin(
      usersToRemove.map((userId: string) =>
        this.taskService.removeAssignedUsers(userId, this.hierarchy, this.team.id),
      ),
    );
  }

  onDeleteUsers() {
    const confirmButtonText = 'Remove';
    const cancelError = 'cancel';

    const promiseConfirm = from(
      this.confirmService.confirmDelete(this.getConfirmMessage(), { confirmButtonText }),
    );
    promiseConfirm
      .pipe(
        switchMap(this.bulkRemoveUsers.bind(this)),
        switchMap(this.getAssignedUsers.bind(this)),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (users) => {
          this.assignedUsers = users;
          this.updateUsersLists(users);
          this.checkedUsers.clear();
          this.isLoading = false;
          void EventNotifierService.notify(
            new DashpivotEvent(EventTypes.FormsUserRemoved, {
              Context: 'Form User',
              Hierarchy: this.hierarchy,
            }),
            this.segmentService,
          );
        },
        (error) => {
          this.isLoading = false;

          if (error !== cancelError) {
            this.errorHandler.handle(error);
          }
        },
      );
  }

  private getConfirmMessage(): string {
    const messages = {
      [AppHierarchy.Organisation]: 'deletedAssignedUsersCompany',
      [AppHierarchy.Project]: 'deletedAssignedUsersTeam',
      [AppHierarchy.Team]: 'deletedAssignedUsersTeam',
    };

    return messages[this.hierarchy];
  }

  onSort(column: string): void {
    if (this.sortingColumn !== column) {
      this.currentSortOrder = this.defaultSortOrder;
    } else {
      this.currentSortOrder = this.currentSortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
    }

    this.sortingColumn = column;
    this.filteredList = this.userListService.sortListByDirection(
      this.filteredList,
      this.sortingColumn,
      this.currentSortOrder,
    );
  }

  updateUsersLists(userList: ISitemateAssignedUserWithTaskUrls[]) {
    const treatedList = this.treatUserListForSorting(userList);
    this.sortedList = this.userListService.sortListByDirection(
      treatedList,
      this.sortingColumn,
      this.currentSortOrder,
    );

    this.filteredList = this.userListService.filterList(this.sortedList, this.searchTerm);
  }

  onSearch() {
    this.filteredList = this.userListService.filterList(this.sortedList, this.searchTerm);
  }

  treatUserListForSorting(list: ISitemateAssignedUserWithTaskUrls[]): IUser[] {
    return list.map((user): IUser => {
      const { firstName, lastName, avatarUrl, companyName } = user.display;
      const taskLinks = user.taskUrls.length;
      return {
        firstName,
        lastName,
        avatarUrl,
        companyName,
        id: user.userId,
        taskLinks,
      };
    });
  }

  onCheck(user: IUser) {
    if (this.checkedUsers.has(user.id)) {
      this.checkedUsers.delete(user.id);
    } else {
      this.checkedUsers.add(user.id);
    }
  }

  onCheckAll() {
    if (this.checkedUsers.size === this.sortedList.length) {
      this.checkedUsers.clear();
    } else {
      this.sortedList.forEach((user) => {
        this.checkedUsers.add(user.id);
      });
    }
  }

  loadTemplatesAndLinks() {
    if (this.appHierarchy.Team === this.hierarchy) {
      this.linksService.retrieveLinks(this.hierarchyId);
      this.loadingTemplates = true;
      const templates$ = this.templatesService.getTemplatesByTeam(this.hierarchyId);
      const currentTeam$ = this.teamService.currentTeam;

      combineLatest([templates$, currentTeam$])
        .pipe(
          takeUntil(this.destroy$),
          filter(([templates, team]) => templates && team?.id && team?.isTeam),
        )
        .subscribe(
          ([templates, team]) => {
            this.routeParamsService.set('templates', templates);
            this.routeParamsService.set('companyFromTeam', team.companyMetadata);
            this.loadingTemplates = false;
          },
          (error) => {
            this.errorHandler.handle(error);
          },
        );
    }
  }

  openUpgradeModal() {
    this.accountUpgradeLimitService.openModal(AccountUpgradeLimitModalContext.SitematePaidUsers);
  }
}
